import {
  CALL_FIELD_RESOLUTION_STAGES,
  NAMESPACE_FIELDS,
  NAMESPACE_RESOLUTION_STAGE,
  EDITOR_FIELD_SCOPED_NAMESPACED_VARIABLES as FIELD_SCOPED_NAMESPACED_VARIABLES,
  EDITOR_FIELD_SCOPED_LEGACY_VARIABLES as FIELD_SCOPED_LEGACY_VARIABLES,
  EDITOR_SYSTEM_VARIABLE_NAMES as SYSTEM_VARIABLE_NAMES,
} from 'utils/variableMetadata.generated';
import {
  liveVariableNames,
  splitNamespace,
} from 'utils/templateVariableParsing';
import {
  ResolutionStage,
  type ResolutionStage as ResolutionStageValue,
} from 'api/generated/types';
import {
  hasJobVariableValues,
  isResolutionStageAvailable,
  isDeprecatedVariableName,
} from 'utils/variableValuePrimitives';
import {
  JOB_EXTERNAL_NAME_VARIABLE,
  JOB_EXTERNAL_NAME_TAIL,
  JOB_INTERNAL_NAME_TAIL,
  JOB_NAME_VALUE_KEY,
  JOB_AI_NAME_VARIABLE,
  AI_NAME_VALUE_KEY,
  AI_NAME_LEGACY_KEY,
  JOB_HIRING_COMPANY_NAME_VARIABLE,
  JOB_HIRING_COMPANY_NAME_TAIL,
  COMPANY_NAME_VALUE_KEY,
  LEGACY_COMPANY_NAME_FALLBACK_KEY,
  CANDIDATE_FULL_NAME_VARIABLE,
  CANDIDATE_FIRST_NAME_VARIABLE,
  CANDIDATE_NAME_VALUE_KEY,
  CANDIDATE_PHONE_NUMBER_VARIABLE,
  CANDIDATE_PHONE_NUMBER_VALUE_KEY,
  CANDIDATE_PHONE_LEGACY_KEY,
  CALL_INTERVIEW_LINK_VARIABLE,
  INTERVIEW_LINK_VALUE_KEY,
  CALL_ESTIMATED_DURATION_MINUTES_VARIABLE,
  ESTIMATED_DURATION_MINUTES_VALUE_KEY,
  CALL_EXPIRATION_DAYS_VARIABLE,
  EXPIRATION_DAYS_VALUE_KEY,
  CALL_RECRUITER_PHONE_NUMBER_VARIABLE,
  RECRUITER_PHONE_NUMBER_VALUE_KEY,
  FOLLOWUP_INTERVIEWER_NAME_VARIABLE,
  INTERVIEWER_NAME_VALUE_KEY,
  FOLLOWUP_INTERVIEWER_EMAIL_VARIABLE,
  INTERVIEWER_EMAIL_VALUE_KEY,
} from 'utils/variableValueKeys';

/** Bare job-attribute keys from a resolved job map (excludes system + org-var names). */
export const jobAttributeNamesFromValues = (
  values: Record<string, string> | null | undefined,
  organizationVariableNames: ReadonlySet<string>
): string[] => {
  if (!values) {
    return [];
  }
  const names = Object.keys(values).filter(
    (name) =>
      splitNamespace(name) === null &&
      !SYSTEM_VARIABLE_NAMES.has(name) &&
      !organizationVariableNames.has(name)
  );
  // Only the explicit ATS value proves that this tail exists; the bare key is the brand.
  if (Object.prototype.hasOwnProperty.call(values, 'job.company_name')) {
    names.push(COMPANY_NAME_VALUE_KEY);
  }
  return names;
};

/**
 * True when `name` is a recognized namespaced variable, i.e. `<prefix>.<tail>` where the tail is in
 * that namespace's allowlist.
 *
 * `job.<tail>` has no fixed allowlist: it ALIASES the un-namespaced job attribute (`job.location.
 * state` -> `location.state` on the backend), so its tail is validated against `unnamespacedAllowed`
 * — the same merged allowed list that already carries every resolved job-attribute key. When no
 * job attributes are loaded (and no catalog allowlist is available), any dotted `job.*` tail is
 * accepted, mirroring the backend treating an unknown dotted name as a config-stage job attribute
 * rather than a typo. Callers with a resolved job map — or a non-empty catalog of org-schema job
 * paths — pass that presence separately because a valid loaded job may contain only flat keys, and
 * org-level editors have a catalog even with no resolved job map.
 *
 * Returns false for a non-namespaced name, so every pre-existing dotted job attribute
 * (`location.state`, `ats.company`) and a Lookup Variable `label.column` keeps its old classification.
 */
export const isRecognizedNamespacedVariable = (
  name: string,
  unnamespacedAllowed: readonly string[] = [],
  jobAttributesLoaded = unnamespacedAllowed.some((allowed) =>
    allowed.includes('.')
  ),
  maxResolutionStage: ResolutionStageValue = ResolutionStage.call
): boolean => {
  const split = splitNamespace(name);
  if (
    split === null ||
    (split.prefix === 'job' && split.tail === LEGACY_COMPANY_NAME_FALLBACK_KEY)
  ) {
    return false;
  }
  const callFieldStage =
    split.prefix === 'call'
      ? CALL_FIELD_RESOLUTION_STAGES[split.tail]
      : undefined;
  const variableStage =
    callFieldStage ?? NAMESPACE_RESOLUTION_STAGE[split.prefix];
  if (!isResolutionStageAvailable(variableStage, maxResolutionStage)) {
    return false;
  }
  if (split.prefix === 'job') {
    return (
      name === JOB_AI_NAME_VARIABLE ||
      split.tail === JOB_EXTERNAL_NAME_TAIL ||
      split.tail === JOB_HIRING_COMPANY_NAME_TAIL ||
      ((split.tail === JOB_INTERNAL_NAME_TAIL || split.tail === 'name') &&
        (unnamespacedAllowed.includes('name') ||
          unnamespacedAllowed.includes('internal_name'))) ||
      ((split.tail === COMPANY_NAME_VALUE_KEY ||
        !SYSTEM_VARIABLE_NAMES.has(split.tail)) &&
        unnamespacedAllowed.includes(split.tail)) ||
      !jobAttributesLoaded
    );
  }
  return NAMESPACE_FIELDS[split.prefix].has(split.tail);
};

/**
 * Names whose values may be absent for static placeholder-type analysis. Exact legacy Organization Variable names
 * always retain Organization Variable semantics, while recognized namespaced names join them only when this field's
 * resolution-stage gate permits that namespace.
 */
export const partialVariableNames = (
  variableNames: Iterable<string>,
  organizationVariableNames: ReadonlySet<string>,
  unnamespacedAllowed: readonly string[] = [],
  supportsNamespacedVariables = true,
  jobAttributesLoaded = unnamespacedAllowed.some((allowed) =>
    allowed.includes('.')
  ),
  maxNamespacedResolutionStage: ResolutionStageValue = ResolutionStage.call
): ReadonlySet<string> => {
  const partialNames = new Set(organizationVariableNames);
  if (!supportsNamespacedVariables) {
    return partialNames;
  }
  for (const name of variableNames) {
    if (
      name !== JOB_AI_NAME_VARIABLE &&
      name !== JOB_EXTERNAL_NAME_VARIABLE &&
      name !== JOB_HIRING_COMPANY_NAME_VARIABLE &&
      name !== CANDIDATE_FIRST_NAME_VARIABLE &&
      !organizationVariableNames.has(name) &&
      isRecognizedNamespacedVariable(
        name,
        unnamespacedAllowed,
        jobAttributesLoaded,
        maxNamespacedResolutionStage
      )
    ) {
      partialNames.add(name);
    }
  }
  return partialNames;
};

/**
 * Merge resolved job/system variable keys into an allowed-variables list.
 *
 * NAMESPACED keys are deliberately EXCLUDED here: a resolved map may contain `candidate.first_name`
 * or `call.status`, and admitting those as job attributes made ANY dotted name present in the map
 * validate as allowed — including tails outside the backend allowlist. Namespaced names are instead
 * classified by `isRecognizedNamespacedVariable`, which checks the tail. Un-namespaced dotted keys
 * (`location.state`, `ats.company`, `custom_field_values.x`) are merged exactly as before.
 */
export const mergeJobAttributeKeysIntoAllowedList = (
  baseAllowed: string[],
  jobVariableValues: Record<string, string> | null | undefined
): string[] => {
  if (!hasJobVariableValues(jobVariableValues)) {
    return baseAllowed;
  }

  const extras = Object.keys(jobVariableValues).filter(
    (key) =>
      !baseAllowed.includes(key) &&
      // A preview value does not grant a field access to structural send variables.
      !FIELD_SCOPED_LEGACY_VARIABLES.has(key) &&
      key !== LEGACY_COMPANY_NAME_FALLBACK_KEY &&
      splitNamespace(key) === null
  );
  return extras.length > 0 ? [...baseAllowed, ...extras] : baseAllowed;
};

/**
 * True when one live operand name is valid in this field: an allowed (or merged job-attribute)
 * name, a recognized Lookup Variable `label.column`, or a recognized `<namespace>.<tail>` reference. An unknown
 * tail under a known prefix (`call.bogus`) matches none of these and stays invalid.
 */
export const isAllowedVariableName = (
  name: string,
  allowed: readonly string[],
  organizationVariableNames: ReadonlySet<string>,
  // Fields that opt out of send-time substitution (supportsOrganizationVariables=false, e.g. Additional
  // Policies / Suggested Follow-Up) don't resolve namespaced variables either, so they must not
  // validate `{{candidate.first_name}}` as recognized — otherwise a namespaced name saves clean
  // and reaches the LLM as literal braces. Callers that zero the Organization Variable set for such fields pass
  // false here too. Defaults true so existing callers keep offering namespaced variables.
  supportsNamespacedVariables = true,
  jobAttributesLoaded = allowed.some((candidate) => candidate.includes('.')),
  maxNamespacedResolutionStage: ResolutionStageValue = ResolutionStage.call,
  jobAttributeNames: readonly string[] = allowed,
  deprecatedVariables?:
    | ReadonlyMap<string, string>
    | ReadonlySet<string>
    | Readonly<Record<string, string>>
): boolean =>
  allowed.includes(name) ||
  (name === JOB_AI_NAME_VARIABLE &&
    (allowed.includes(AI_NAME_VALUE_KEY) ||
      allowed.includes(AI_NAME_LEGACY_KEY))) ||
  ((name === AI_NAME_VALUE_KEY || name === AI_NAME_LEGACY_KEY) &&
    (allowed.includes(JOB_AI_NAME_VARIABLE) ||
      allowed.includes(AI_NAME_VALUE_KEY) ||
      allowed.includes(AI_NAME_LEGACY_KEY))) ||
  // The title substitutes wherever its alias `job_name` does, including fields that don't
  // resolve other namespaced names.
  (name === JOB_EXTERNAL_NAME_VARIABLE &&
    allowed.includes(JOB_NAME_VALUE_KEY)) ||
  (name === JOB_NAME_VALUE_KEY &&
    allowed.includes(JOB_EXTERNAL_NAME_VARIABLE)) ||
  (name === JOB_HIRING_COMPANY_NAME_VARIABLE &&
    allowed.includes(COMPANY_NAME_VALUE_KEY)) ||
  (name === COMPANY_NAME_VALUE_KEY &&
    allowed.includes(JOB_HIRING_COMPANY_NAME_VARIABLE)) ||
  // Fields that only list `candidate_name` still take the full name explicitly.
  (name === CANDIDATE_FULL_NAME_VARIABLE &&
    allowed.includes(CANDIDATE_NAME_VALUE_KEY)) ||
  (name === CANDIDATE_NAME_VALUE_KEY &&
    (allowed.includes(CANDIDATE_FIRST_NAME_VARIABLE) ||
      allowed.includes(CANDIDATE_FULL_NAME_VARIABLE))) ||
  (name === CANDIDATE_PHONE_NUMBER_VARIABLE &&
    (allowed.includes(CANDIDATE_PHONE_NUMBER_VALUE_KEY) ||
      allowed.includes(CANDIDATE_PHONE_LEGACY_KEY))) ||
  ((name === CANDIDATE_PHONE_NUMBER_VALUE_KEY ||
    name === CANDIDATE_PHONE_LEGACY_KEY) &&
    allowed.includes(CANDIDATE_PHONE_NUMBER_VARIABLE)) ||
  (name === CALL_INTERVIEW_LINK_VARIABLE &&
    allowed.includes(INTERVIEW_LINK_VALUE_KEY)) ||
  (name === INTERVIEW_LINK_VALUE_KEY &&
    allowed.includes(CALL_INTERVIEW_LINK_VARIABLE)) ||
  (name === CALL_ESTIMATED_DURATION_MINUTES_VARIABLE &&
    allowed.includes(ESTIMATED_DURATION_MINUTES_VALUE_KEY)) ||
  (name === ESTIMATED_DURATION_MINUTES_VALUE_KEY &&
    allowed.includes(CALL_ESTIMATED_DURATION_MINUTES_VARIABLE)) ||
  (name === CALL_EXPIRATION_DAYS_VARIABLE &&
    allowed.includes(EXPIRATION_DAYS_VALUE_KEY)) ||
  (name === EXPIRATION_DAYS_VALUE_KEY &&
    allowed.includes(CALL_EXPIRATION_DAYS_VARIABLE)) ||
  (name === CALL_RECRUITER_PHONE_NUMBER_VARIABLE &&
    allowed.includes(RECRUITER_PHONE_NUMBER_VALUE_KEY)) ||
  (name === RECRUITER_PHONE_NUMBER_VALUE_KEY &&
    allowed.includes(CALL_RECRUITER_PHONE_NUMBER_VARIABLE)) ||
  (name === FOLLOWUP_INTERVIEWER_NAME_VARIABLE &&
    allowed.includes(INTERVIEWER_NAME_VALUE_KEY)) ||
  (name === INTERVIEWER_NAME_VALUE_KEY &&
    allowed.includes(FOLLOWUP_INTERVIEWER_NAME_VARIABLE)) ||
  (name === FOLLOWUP_INTERVIEWER_EMAIL_VARIABLE &&
    allowed.includes(INTERVIEWER_EMAIL_VALUE_KEY)) ||
  (name === INTERVIEWER_EMAIL_VALUE_KEY &&
    allowed.includes(FOLLOWUP_INTERVIEWER_EMAIL_VARIABLE)) ||
  organizationVariableNames.has(name) ||
  // A deprecated system name (`job_name`, `candidate_name`) stays valid only where the field allows it.
  (isDeprecatedVariableName(name, deprecatedVariables) &&
    !SYSTEM_VARIABLE_NAMES.has(name)) ||
  (supportsNamespacedVariables &&
    !FIELD_SCOPED_NAMESPACED_VARIABLES.has(name) &&
    isRecognizedNamespacedVariable(
      name,
      jobAttributeNames.filter(
        (candidate) => !organizationVariableNames.has(candidate)
      ),
      jobAttributesLoaded,
      maxNamespacedResolutionStage
    ));

export const textUsesOnlyAllowedVariables = (
  text: string,
  baseAllowed: string[],
  jobVariableValues?: Record<string, string> | null,
  organizationVariableNames: ReadonlySet<string> = new Set<string>(),
  supportsNamespacedVariables = true,
  maxNamespacedResolutionStage: ResolutionStageValue = ResolutionStage.call,
  // Explicit catalog/org-schema job attribute paths. Do NOT derive these from
  // baseAllowed — that list also carries generation extras (e.g. generated_question)
  // which must not become valid {{job.<extra>}} aliases.
  catalogJobAttributeNames: ReadonlySet<string> = new Set(),
  deprecatedVariables?:
    | ReadonlyMap<string, string>
    | ReadonlySet<string>
    | Readonly<Record<string, string>>
): boolean => {
  const allowed = mergeJobAttributeKeysIntoAllowedList(
    baseAllowed,
    jobVariableValues
  );
  // job.* tails = resolved job-map keys ∪ explicit catalog paths only.
  const jobAttributeNames = [
    ...new Set([
      ...jobAttributeNamesFromValues(
        jobVariableValues,
        organizationVariableNames
      ),
      ...catalogJobAttributeNames,
    ]),
  ];
  // Org-level editors (custom-var prompts, admin templates) have no resolved job map, but the
  // unified catalog still supplies the org-schema job/ATS paths — treat that set as loaded so
  // {{job.not_a_real_field}} is rejected instead of accepted via the permissive fallback.
  const jobAttributesLoaded =
    hasJobVariableValues(jobVariableValues) ||
    catalogJobAttributeNames.size > 0;
  const matches = text.match(/\{\{([^}]+)\}\}/g);
  if (!matches) {
    return true;
  }

  return matches.every((match) =>
    // Validate every LIVE operand (head + non-head `or` operands up to the first literal), so a
    // typo in a fallback operand isn't hidden by a valid head. Recognized Organization Variables
    // (label.column) and recognized `<namespace>.<tail>` references are valid too — both resolve
    // later, matching the coloring path.
    liveVariableNames(match.slice(2, -2)).every((name) =>
      isAllowedVariableName(
        name,
        allowed,
        organizationVariableNames,
        supportsNamespacedVariables,
        jobAttributesLoaded,
        maxNamespacedResolutionStage,
        jobAttributeNames,
        deprecatedVariables
      )
    )
  );
};
